import type { Catalog } from "../en";

/** Filipino: onboarding wizard (mirrors en/onboarding.ts; industry examples come from template data). */
export const onboarding: Catalog["onboarding"] = {
  title: "Pag-set up ng workspace · {{product}}",
  setupOf: "Pag-set up ng workspace ·",
  stepCount: "Hakbang {{current}} sa {{total}}",
  saveExit: "I-save at lumabas",
  saved: "Na-save ang progreso — tapusin ang setup kahit kailan mula sa dashboard",
  stepsLabel: "Mga hakbang sa setup",
  stepDone: "(tapos na)",
  steps: {
    organization: "Organisasyon",
    knowledge: "Magdagdag ng kaalaman",
    configure: "I-configure",
    test: "Subukan",
    live: "I-live",
  },
  back: "Bumalik",
  skip: "Laktawan ang setup",
  industries: {
    education: "Edukasyon (paaralan at unibersidad)",
    healthcare: "Healthcare at klinika",
    real_estate: "Real estate",
    hospitality: "Hospitality at resort",
    human_resources: "Human resources / internal help desk",
    government: "Pamahalaan at serbisyong pampubliko",
    ecommerce: "E-commerce at retail",
    business: "Negosyo at propesyonal na serbisyo",
    other: "Iba pa",
  },
  org: {
    regionLegend: "Bansa, currency at oras",
    country: "Bansa",
    currency: "Currency ng billing",
    currencyHint: "Pilipinas: piso sa GCash, Maya o card. Sa ibang bansa: US dollar sa card o PayPal.",
    timezone: "Time zone",
    language: "Default na wika",
    heading: "Kuwentuhan kami tungkol sa iyong organisasyon",
    subtitle: "Ang mga sagot mo ang pipili ng panimulang setup. Mababago mo ang lahat mamaya sa Settings.",
    industry: "Anong klaseng organisasyon ito?",
    industryRequired: "Pumili ng industriya",
    industryOther: "Ang iyong industriya",
    industryOtherHint: "hal. Logistics, Non-profit, Law firm",
    industryOtherRequired: "Sabihin ang iyong industriya",
    goals: "Ano ang dapat gawin ng assistant?",
    goalsHint: "Piliin lahat ng angkop — ito ang huhubog sa inirerekomendang setup.",
    goalsError: "Hindi ma-load ang mga layunin. Puwede kang magpatuloy at pumili mamaya sa Settings.",
    name: "Pangalan ng organisasyon",
    website: "Website URL",
    websiteHint: "Magmumungkahi kami ng mga pahinang ika-crawl sa susunod na hakbang.",
    websiteInvalid: "Maglagay ng domain gaya ng example.com",
    city: "Lungsod",
    address: "Address ng workspace",
    addressHint: "Dito bubuksan ng mga tao ang iyong assistant.",
    slugTaken: "May gumagamit na ng address na iyan. Subukan ang iba.",
    slugReserved: "Naka-reserve ang address na iyan. Subukan ang iba.",
    slugAvailable: "Available",
    slugChecking: "Sinusuri…",
    slugInvalid: "Gumamit ng 3–40 maliliit na letra, numero o gitling",
    details: "Ilang detalye",
    fieldRequired: "Sagutin ang tanong na ito",
    logo: "Logo",
    logoUpload: "Mag-upload ng logo",
    logoReplace: "Palitan ang logo",
    logoRemove: "Alisin",
    logoHint: "PNG, JPEG o WebP, parisukat, hanggang 2 MB",
    logoPending: "Ia-upload pagpatuloy mo",
    logoUploaded: "Na-upload ang logo",
    logoFailed: "Hindi na-upload ang logo. Subukan ulit o idagdag mamaya sa Settings.",
    logoBadType: "Mag-upload ng PNG, JPEG o WebP na larawan",
    logoTooLarge: "Masyadong malaki ang larawan",
    logoAlt: "Logo ng {{name}}",
    describe: "Ilarawan ang gusto mo (opsyonal)",
    describeHint: "Sino ang gagamit ng assistant, ano ang dapat nitong sagutin at ano ang susunod na mangyayari. Itinuturing namin itong impormasyon, hindi utos.",
    describeCount: "{{count}} / 1,000",
    generate: "Gawin ang aking setup",
    generateHint: "Gagawa ng draft ng assistant, mga tanong, audiences at lead pipeline para suriin mo.",
    templatesHeading: "Magsimula sa isang template",
    templatesSubtitle: "Pinupunan ng mga template ang mga tagubilin, tanong, audiences at lead pipeline ng assistant.",
    templatesLabel: "Panimulang template",
    recommended: "Inirerekomenda",
    templatesError: "Hindi ma-load ang mga template. Puwede kang magpatuloy sa blangkong assistant.",
    templatesNeedIndustry: "Piliin muna ang iyong industriya — itinutugma rito ang mga template.",
    templateChosen: "Pinili mo ang “{{chosen}}”. Ang “{{recommended}}” ang pinakaangkop sa mga sagot mo.",
    useRecommended: "Gamitin ang inirerekomenda",
    yourChoice: "Pinili mo",
    setsUp: "Ang inaayos ng “{{template}}”",
    setsUpAssistant: "Assistant",
    setsUpStarters: "Mga panimulang tanong",
    setsUpAudiences: "Mga pinaglilingkurang audience",
    setsUpPipeline: "Lead pipeline",
    setsUpActions: "Mga aksyon",
    setsUpFootnote: "Maaari mong suriin at baguhin ang lahat ng ito sa hakbang na I-configure.",
    actionOn: "Naka-on",
    actionOff: "Naka-off",
    yourOrganization: "ang inyong organisasyon",
    templateLoading: "Nilo-load ang detalye ng template na “{{template}}”…",
    templateLoaded: "Na-load na ang detalye ng template na “{{template}}”.",
    templateDetailsError: "Hindi ma-load ang detalye ng template na ito.",
    retry: "Subukan ulit",
    detailsHint: "Sumusunod ang mga tanong na ito sa piniling template: {{template}}.",
    next: "Susunod: Magdagdag ng kaalaman",
    created: "Nagawa ang workspace",
  },
  knowledge: {
    heading: "Idagdag ang iyong kaalaman",
    subtitle: "Sumasagot lang ang assistant mula sa idinagdag mo rito, at binabanggit ang mismong pahina. Magsimula sa mga dokumentong madalas itanong.",
    examples: "Magandang unang sources: {{examples}}",
    tabFiles: "Mag-upload ng file",
    tabCrawl: "I-crawl ang website",
    tabQa: "Sumulat ng Q&A",
    dropTitle: "I-drag ang mga file dito o mag-browse",
    dropHint: "PDF, DOCX, XLSX, PPTX, CSV, TXT, mga larawan (OCR) · hanggang 50 MB bawat isa",
    filesNote: "Naka-encrypt ang mga file sa iyong workspace lang at hindi kailanman ginagamit sa pag-train ng pampublikong AI model. Alisin ang {{sensitive}} sa mga dokumentong para sa pampublikong assistant.",
    sensitiveDefault: "personal na datos",
    moreLater: "Nakikita ng lahat ang mga bagong source hangga't hindi mo nililimitahan. Puwede kang magdagdag pa ng source at baguhin kung sino ang nakakakita sa bawat isa anumang oras sa Knowledge Base.",
    waitUploads: "Hinihintay matapos ang mga upload…",
    retry: "Subukan muli",
    retrySource: "Subukan muli ang {{name}}",
    uploadsLabel: "Mga file na ina-upload",
    sourcesHeading: "Ang iyong mga source",
    sourcesLabel: "Mga source na naidagdag na",
    sourcesErrorTitle: "Hindi namin ma-load ang iyong mga source",
    sourcesErrorBody: "Ligtas ang iyong mga upload. Tingnan ang koneksyon at subukan muli.",
    sourcesRetry: "Subukan muli",
    sourceFailedFallback: "Hindi napoproseso ang source na ito. Subukan muli, o palitan ito sa Knowledge Base.",
    emptyTitle: "Wala pang naidaragdag",
    emptyBody: "Mag-upload ng dokumento, i-crawl ang iyong website o sumulat ng ilang Q&A. Sumasagot lang ang assistant mula sa idinagdag mo.",
    crawlUrlRequired: "Ilagay ang address ng iyong website",
    crawlUrlInvalid: "Maglagay ng buong address tulad ng https://example.com",
    crawlFetching: "Sinusuri ang site…",
    crawlFetchFailed: "Hindi namin masuri ang website na iyon",
    crawlFound_one: "{{count}} pahina ang nakita",
    crawlFound_other: "{{count}} pahina ang nakita",
    crawlFoundCapped: "{{count}}+ pahina ang nakita (naabot ang limitasyon)",
    crawlSelected: "{{count}} ang napili",
    crawlSelectAll: "Piliin lahat",
    crawlClear: "I-clear",
    crawlListLabel: "Mga pahinang idaragdag",
    crawlStartPage: "Panimulang pahina, laging kasama",
    crawlShowing: "Ipinapakita ang unang {{shown}} sa {{total}} pahina. Susundin ng iba ang iyong include at exclude na path.",
    crawlAdd_one: "Magdagdag ng {{count}} pahina",
    crawlAdd_other: "Magdagdag ng {{count}} pahina",
    crawlNothingTitle: "Walang mai-crawl na pahina",
    crawlNothingBody: "Tingnan ang address, o luwagan ang iyong include at exclude na path, saka muling kunin.",
    crawlTooManyExcludes: "Masyadong maraming pahina ang inalis para maipadala bilang exclusion. Mas kaunting pahina ang alisin, o i-exclude ang buong folder gamit ang path tulad ng /news/*.",
    qaListLabel: "Mga Q&A",
    qaSave: "I-save ang Q&A",
    qaSavedBadge: "Na-save",
    qaSaved_one: "{{count}} Q&A ang naidagdag sa iyong Knowledge Base",
    qaSaved_other: "{{count}} na Q&A ang naidagdag sa iyong Knowledge Base",
    qaIncomplete: "May mga Q&A na tanong lang o sagot lang ang laman. Kumpletuhin, o alisin ang Q&A.",
    qaPartial: "{{failed}} sa {{total}} Q&A ang hindi na-save. {{reason}}",
    crawlUrl: "Panimulang URL",
    crawlLimit: "Limitasyon ng pahina",
    crawlPages: "{{count}} pahina",
    crawlInclude: "Isama ang mga path",
    crawlIncludeHint: "Hiwalayin ng kuwit. Iwanang blangko para isama lahat.",
    crawlExclude: "Huwag isama ang mga path",
    crawlFetch: "Kunin ang mga pahina",
    crawlNote: "Sinusunod namin ang robots.txt at nagsi-sync muli lingguhan.",
    qaNote: "Maiikling eksaktong sagot para sa mga bagay na wala pa sa anumang dokumento. Mas inuuna ang Q&A kaysa sa mga file.",
    question: "Tanong",
    answer: "Sagot",
    questionPlaceholder: "hal. {{example}}",
    questionExample: "Ano ang oras ng opisina ninyo?",
    answerPlaceholder: "Isulat ang eksaktong sagot na dapat matanggap…",
    removePair: "Alisin",
    addPair: "Magdagdag ng Q&A",
    pairRemoved: "Inalis ang Q&A",
    next: "Susunod: I-configure",
  },
  configure: {
    heading: "I-configure ang iyong assistant",
    subtitle: "Suriin ang mungkahing setup. Lahat dito ay nae-edit at walang mase-save hangga't hindi mo kinukumpirma.",
    loading: "Inihahanda ang iyong setup…",
    draftError: "Hindi namin naihanda ang setup.",
    retry: "Subukan ulit",
    sourceAi: "Ginawa ng AI mula sa iyong paglalarawan — suriin bago i-save.",
    sourceTemplate: "Batay sa template na “{{template}}” at sa iyong mga layunin.",
    fallback_unavailable: "Hindi available ang AI drafting ngayon, kaya template ang ginamit namin.",
    fallback_invalid: "Hindi magamit ang AI draft, kaya template ang ginamit namin.",
    fallback_daily_cap: "Naabot na ang limitasyon ng AI draft ngayong araw, kaya template ang ginamit namin.",
    describe: "Ilarawan ang gusto mo",
    regenerate: "Gumawa gamit ang AI",
    name: "Pangalan ng assistant",
    purpose: "Template ng layunin",
    purposeHint: "Para saan ang assistant na ito. Ilo-load ang setup ng template kapag pinalitan.",
    purposeRecommended: "{{name}} (inirerekomenda)",
    switching: "Nilo-load ang setup na “{{template}}”…",
    switched: "Napalitan sa setup na “{{template}}”",
    replaceTitle: "Palitan ang mga edit mo ng setup na “{{template}}”?",
    replaceAiTitle: "Palitan ang AI draft ng setup na “{{template}}”?",
    replaceBody: "Papalitan ng setup ng template na ito ang pangalan ng assistant, mga tagubilin, tanong, audiences, lead pipeline, aksyon, welcome message at mga paksang tatanggihan. Hindi na ito maibabalik.",
    replaceConfirm: "Palitan ng template na ito",
    replaceKeep: "Panatilihin ang mayroon ako",
    defaultName: "Assistant",
    instructions: "Persona at saklaw",
    instructionsHelp: "Laging idinadagdag ng platform ang grounding rules — sumagot lang mula sa mga dokumento mo, banggitin ang sources, huwag mag-imbento — at hindi ito maaalis.",
    instructionsCount: "{{count}} / 2,000",
    instructionsTooLong: "Panatilihing wala pang 2,000 character",
    starters: "Mga panimulang tanong",
    starter: "Panimulang tanong {{n}}",
    audiences: "Audiences",
    audiencesHelp: "Palitan ang pangalan ng apat na antas ng audience at piliin kung sino ang pinagsisilbihan ng assistant.",
    audienceLabel: "Pangalan ng audience {{code}}",
    audienceServed: "Pinagsisilbihan ang audience na ito",
    stages: "Lead pipeline",
    stagesHelp: "Dapat bukas ang unang stage, may kahit isang Won at eksaktong isang Lost.",
    stageName: "Pangalan ng stage {{n}}",
    stageKind: "Uri ng stage {{n}}",
    stageOpen: "Bukas",
    stageWon: "Won",
    stageLost: "Lost",
    moveUp: "Iakyat ang {{name}}",
    moveDown: "Ibaba ang {{name}}",
    removeStage: "Alisin ang {{name}}",
    addStage: "Magdagdag ng stage",
    newStage: "Bagong stage",
    stagesInvalid: "Suriin ang pipeline: bukas ang unang stage, kahit isang Won, eksaktong isang Lost, walang pare-parehong pangalan.",
    actions: "Mga aksyon",
    actionsHelp: "Ang mga puwedeng gawin ng assistant. Laging naka-on ang pakikipag-usap sa tao.",
    alwaysOn: "Laging naka-on",
    welcome: "Welcome message",
    topics: "Mga paksang tatanggihan",
    topicsHint: "Isang paksa bawat linya.",
    preview: "Preview",
    tipLead: "Tip:",
    tipBody: "itago sa mga tagubilin ang tungkol sa ugali. Ilagay sa mga dokumento ang mga katotohanan gaya ng presyo at petsa para nababanggit at madaling i-update.",
    confirm: "Gawin ang assistant at magpatuloy",
    created: "Nagawa ang assistant",
    doneTitle: "Naka-set up na ang iyong assistant",
    doneBody: "Ginawa ang {{name}} mula sa setup na ito. Ayusin ito kahit kailan sa Agents.",
    next: "Susunod: Subukan",
  },
  test: {
    heading: "Subukan ito gaya ng isang bisita",
    subtitle: "Magtanong ng ilang totoong tanong. I-click ang citation para makita ang mismong talatang pinagmulan ng sagot.",
    testMode: "Test mode · hindi nakikita ng publiko",
    reset: "I-reset",
    tryTitle: "Subukan ito sa Playground",
    tryBody: "Sumasagot ang iyong assistant mula sa mga idinagdag mong source. Buksan ang Playground para makipag-chat at makita ang buong retrieval trace.",
    openPlayground: "Buksan ang Playground",
    checksTitle: "Ano ang susuriin",
    checkRequirements: "Tugma ang mga sagot sa iyong mga dokumento",
    checkLanguage: "Taglish na sagot sa Taglish na tanong, tama ang mga detalye",
    checkAction: "Lumalabas ang booking o inquiry card kapag handa na ang tao",
    checkGap: "Fallback ang sagot sa hindi alam na tanong, hindi hula",
    tryQuestions: "Mga tanong na susubukan",
    noteLead: "Mali ang sagot? Ayusin ang source na dokumento o magdagdag ng Q&A — magagawa mo ito mamaya sa",
    notePlayground: "Playground",
    noteTail: "kasama ang buong retrieval trace.",
    next: "Susunod: I-live",
  },
  live: {
    heading: "I-live",
    subtitle: "Piliin kung saan ka maaabot ng mga tao. Puwede kang magdagdag ng channel kahit kailan.",
    widget: "Website widget",
    widgetBody: "Chat bubble sa {{site}}. Mag-paste ng isang linya ng code.",
    widgetBodyNoSite: "Chat bubble sa iyong website. Mag-paste ng isang linya ng code.",
    webapp: "Web app link",
    webappBody: "Full-page na chat sa sarili mong link. Maganda para sa QR code sa mga flyer.",
    messenger: "Facebook Messenger",
    messengerBody: "Awtomatikong sagutin ang mga mensahe sa iyong Facebook Page.",
    messengerNone: "Wala pang nakakonektang Page",
    connectPage: "Ikonekta ang Page",
    connectLater: "Ikonekta ang iyong Page mula sa Channels pagkatapos ng setup.",
    mobile: "Mobile app",
    mobileBody: "I-embed sa sarili mong app gamit ang iOS / Android SDK.",
    planBadge: "Standard plan",
    enabled: "Na-enable ang {{channel}}",
    disabled: "Na-disable ang {{channel}}",
    embedTitle: "Embed code",
    embedLead: "I-paste bago ang",
    embedTail: "sa bawat pahina — o ipadala ito sa iyong IT coordinator.",
    emailIt: "I-email sa IT",
    emailItLater: "Puwedeng i-email ang mga tagubilin kapag na-publish na ang assistant.",
    copy: "Kopyahin",
    copied: "Nakopya sa clipboard",
    embedFooter: "Gumagana sa WordPress, Wix, Google Sites at plain HTML. Pinapayagang domain:",
    agentPending: "agt_… (ibibigay kapag na-publish na ang iyong assistant)",
    webappLink: "Web app link",
    open: "Buksan",
    fallbackDomain: "Pansamantala habang sine-set up ang DNS:",
    inviteTeam: "Imbitahan ang iyong team",
    finish: "Tapusin at buksan ang dashboard",
    finished: "Tapos na ang setup — maligayang pagdating sa iyong workspace",
  },
};
